
import { ViewportStoreModule } from "@stores/viewport.store";
import { computed, defineComponent, getCurrentInstance, ref, PropType } from "vue";
import { getModule } from "vuex-module-decorators";
import AISRangeWithPeriod from "@components/base/ais/range-with-period.vue";
import { useI18n } from "@composables/i18n";
import * as vClickOutside from "v-click-outside";
import CiClearRefinements from "@components/base/ais/clear-refinements.vue";
import { settingsService } from "@services/settings.service";

const ViewportStore = getModule(ViewportStoreModule);

export default defineComponent({
  name: "CiPortfoliosComparisonFilter",
  components: {
    "ci-ais-range-with-period": AISRangeWithPeriod,
    "ci-clear-refinements": CiClearRefinements,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    selectedPerformanceInterval: {
      type: Object as PropType<Portfolios.PerformanceInterval>,
      required: true,
    },
  },
  emits: ["sortingChanged", "resetFilters"],
  setup(props, { emit }) {
    const { t } = useI18n(getCurrentInstance());

    const displayFiltersMenu = ref(false);
    const displaySortingMenu = ref(false);

    const closeFiltersMenu = () => {
      displayFiltersMenu.value = false;
    };
    const closeSortingMenu = () => {
      displaySortingMenu.value = false;
    };
    const toggleFiltersMenu = () => {
      displayFiltersMenu.value = !displayFiltersMenu.value;
      if (displayFiltersMenu.value) closeSortingMenu();
    };
    const toggleSortingMenu = () => {
      displaySortingMenu.value = !displaySortingMenu.value;
      if (displaySortingMenu.value) closeFiltersMenu();
    };
    const filterOptions = computed(() => [1, 3, 5, 10].map((val) => `_${val}_year${val > 1 ? "s" : ""}`));
    const rawIndexName: string = settingsService.options.algolia_index_names.portfolios;

    const selectedPeriod = computed(() => {
      const abbreviatedCategoriesMap: Record<string, string> = { month: "m", year: "y", ytd: "ytd" };
      const { period, category } = props.selectedPerformanceInterval;

      const categoryAbbreviation = abbreviatedCategoriesMap[category];
      return period ? `${period}${categoryAbbreviation}` : `${categoryAbbreviation}`;
    });

    const sortingOptions = [
      {
        value: `${rawIndexName}_perf_${selectedPeriod.value}_asc`,
        label: t("ui.components.portfolios.comparisons.sorting.performance_asc"),
        attribute: "perf",
        sorting: "asc",
      },
      {
        value: `${rawIndexName}_perf_${selectedPeriod.value}_desc`,
        label: t("ui.components.portfolios.comparisons.sorting.performance_desc"),
        attribute: "perf",
        sorting: "desc",
      },
      {
        value: `${rawIndexName}_vola_${selectedPeriod.value}_asc`,
        label: t("ui.components.portfolios.comparisons.sorting.volatility_asc"),
        attribute: "vola",
        sorting: "asc",
      },
      {
        value: `${rawIndexName}_vola_${selectedPeriod.value}_desc`,
        label: t("ui.components.portfolios.comparisons.sorting.volatility_desc"),
        attribute: "vola",
        sorting: "desc",
      },
      {
        value: `${rawIndexName}_followers_count_desc_asc`,
        label: t("ui.components.portfolios.comparisons.sorting.followers_count_desc"),
        attribute: "followers_count",
        sorting: "desc",
      },
    ];
    const currentSortingSelected = ref(sortingOptions[1]);
    const handleSortingSelected = (option: any) => {
      currentSortingSelected.value = option;
      emit("sortingChanged", currentSortingSelected.value);
      if (!mobileMode.value) closeSortingMenu();
    };
    const mobileMode = computed(() => ViewportStore.smartphone);

    const mobileMenu = ref(null);
    const menuOpened = ref(false);
    const closeMenu = () => {
      toggleMenu();
      if (!menuOpened.value) emit("resetFilters");
    };
    const toggleMenu = () => {
      const { body } = document;
      if (!menuOpened.value) {
        menuOpened.value = true;
        displayFiltersMenu.value = true;
        displaySortingMenu.value = false;
        body.style.overflow = "hidden";
      } else {
        displayFiltersMenu.value = false;
        displaySortingMenu.value = false;
        menuOpened.value = false;
        body.style.overflow = "auto";
      }
    };
    const optionSelected = ref(currentSortingSelected.value.label);

    const currentFilters = (items: any[]) => {
      if (items.length === 0) return null;

      return items.map((item) => t(`ui.views.portfolios.comparisons.${item.label}`)).join(", ");
    };

    return {
      mobileMode,
      filterOptions,
      displayFiltersMenu,
      displaySortingMenu,
      toggleFiltersMenu,
      sortingOptions,
      currentSortingSelected,
      handleSortingSelected,
      toggleSortingMenu,
      menuOpened,
      toggleMenu,
      mobileMenu,
      optionSelected,
      closeMenu,
      currentFilters,
      closeFiltersMenu,
      closeSortingMenu,
    };
  },
});
